import { type PageViewport, type PDFPageProxy, TextLayer } from 'pdfjs-dist';
import { memo, useRef } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { PDFPageTextLayerEnd } from './components/PDFPageTextLayerEnd';
import { useHighlightSearchResults } from './hooks/useHighlightSearchResults';

type PDFPageTextLayerProps = {
  pdfPage: PDFPageProxy;
  pageViewport: PageViewport;
  pageNumber: number;
};

export const PDFPageTextLayer = memo(({ pdfPage, pageViewport, pageNumber }: PDFPageTextLayerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const textLayerRef = useRef<TextLayer>();

  const isMounted = useRef(false);

  const { onTextLayerRendered } = useHighlightSearchResults({ containerRef, pageNumber });

  const onTextLayerRenderedRef = useRef(onTextLayerRendered);
  onTextLayerRenderedRef.current = onTextLayerRendered;

  useIsomorphicLayoutEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useIsomorphicLayoutEffect(() => {
    const renderTextLayer = async () => {
      if (!containerRef.current) {
        return;
      }

      if (textLayerRef.current) {
        textLayerRef.current.update({
          onBefore: () => {
            if (containerRef.current) {
              containerRef.current.hidden = true;
            }
          },
          viewport: pageViewport,
        });

        containerRef.current.hidden = false;
        return;
      }

      containerRef.current.hidden = true;
      textLayerRef.current = new TextLayer({
        container: containerRef.current,
        textContentSource: pdfPage.streamTextContent({
          disableNormalization: true,
          includeMarkedContent: true,
        }),
        viewport: pageViewport,
      });

      try {
        await textLayerRef.current.render();

        if (!isMounted.current) {
          return;
        }

        onTextLayerRenderedRef.current(textLayerRef.current.textDivs, textLayerRef.current.textContentItemsStr);

        containerRef.current.hidden = false;
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortException') {
          return;
        }
        console.error('Error rendering text layer', error);
      }
    };

    void renderTextLayer();

    return () => {
      textLayerRef.current?.cancel();
    };
  }, [pageViewport, pdfPage]);

  return (
    <div className="pdfTextLayer" ref={containerRef}>
      <PDFPageTextLayerEnd />
    </div>
  );
});

PDFPageTextLayer.displayName = 'PDFPageTextLayer';
